"use client";
import { FC, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import BFLLink from "@repo/shared-ui/BFLLink";
import Conditional from "@repo/shared-ui/Conditional";
import MenuToggle from "./MenuToogle";
import {
  TopNavItem,
  QuickLink,
  TertiaryDetail,
} from "../../interfaces/IHeader.interface";

interface LeftMenuProps {
  navItems?: TopNavItem[];
  bottomnavigationmobile?: any;
}

// Render quickLinks section below tertiaryNav
const renderQuickLinks = (
  quickLinks: QuickLink[],
  quickLinksHeading: string,
) => {
  if (!quickLinks || quickLinks.length === 0) return null;
  return (
    <>
      {quickLinksHeading && (
        <h3 className="mb-[18px] text-sm font-[500] uppercase text-blue-400">
          {quickLinksHeading}
        </h3>
      )}
      <ul className="flex flex-wrap w-165">
        {quickLinks.map((ql: any, idx: number) => (
          <li
            className="mb-2 mr-2"
            key={`${ql.quickLinksCtaLink || ql.quickLinksTitle || "quick-link"}-${idx}`}
          >
            <BFLLink
              href={ql.quickLinksCtaLink}
              openInNewTab={ql.quickLinksCtaNewTab === "true"}
              className="mb-2 block cursor-pointer rounded-[12px] bg-[#1a2734] px-2 py-1 text-xs"
            >
              {ql.quickLinksTitle}
            </BFLLink>
          </li>
        ))}
      </ul>
    </>
  );
};

const renderTertiaryColumn = (tertiary: any, idx: number) => {
  const tertiaryDetails =
    tertiary?.children?.find(
      (child: any) => child.nodeName === "tertiaryDetails",
    )?.children || [];
  const columnTitle = tertiary?.columnTitle;

  return (
    <div
      className="tertiary-menu__column min-w-[280px]"
      key={`${tertiary.id || tertiary.nodeName || "tertiary-column"}-${idx}`}
    >
      {columnTitle && (
        <h4 className="tertiary-menu__column-title mb-[18px] text-sm font-[500] uppercase text-blue-400">
          {columnTitle}
        </h4>
      )}
      {tertiaryDetails.map((td: any, idx: number) => (
        <li
          className="tertiary-menu__column-item whitespace-nowrap"
          key={`${td.tertiaryCtaLink || td.tertiaryLabel || "tertiary-item"}-${idx}`}
        >
          <BFLLink
            href={td.tertiaryCtaLink}
            openInNewTab={td.tertiaryCtaInNewTab === "true"}
            className="tertiary-menu__column-link mb-4 block cursor-pointer whitespace-nowrap text-sm font-normal transition-all duration-200 hover:font-[600]"
          >
            {td.tertiaryLabel}
          </BFLLink>
        </li>
      ))}
    </div>
  );
};

const renderTertiaryNavWithQuickLinks = (
  tertiaryNav: TertiaryDetail[],
  quickLinks: QuickLink[],
  quickLinksHeading: string,
  parentNavTitle?: string, // Add parent nav title parameter
  openOnLeft?: boolean,
  forceOpen?: boolean,
  matchedHeight?: number | null,
) => {
  // Don't add fixed height for Services menu
  const isServicesMenu = parentNavTitle === "Services";
  const tertiaryColumnCount = Math.max(
    1,
    Math.min(2, tertiaryNav.length + (quickLinks.length > 0 ? 1 : 0)),
  );

  return (
    <ul
      className={`tertiary-menu tertiary-menu--expand nav-menu-bar-style absolute top-0 min-w-[280px] overflow-y-auto overscroll-contain bg-blue-900 ${
        forceOpen ? "block" : "hidden group-hover/submenu:block"
      } ${
        openOnLeft ? "right-full" : "left-full"
      }`}
      style={{
        top: 0,
        [openOnLeft ? "right" : "left"]: "100%",
        width: "max-content",
        minWidth: "280px",
        maxHeight: "82vh",
        ...(matchedHeight ? { minHeight: `${matchedHeight}px` } : {}),
      }}
    >
      <div
        className="tertiary-menu__columns"
        style={{
          display: "inline-grid",
          gridTemplateColumns: isServicesMenu
            ? "minmax(280px, max-content)"
            : `repeat(${tertiaryColumnCount}, minmax(280px, max-content))`,
          gap: "64px",
          padding: "10px 16px",
          width: "max-content",
          minWidth: "280px",
          alignContent: "start",
        }}
      >
        {tertiaryNav.map((tertiary: any, tIdx: number) =>
          renderTertiaryColumn(tertiary, tIdx),
        )}

        {quickLinks.length > 0 && (
          <div
            className="tertiary-menu__column"
            style={{
              gridColumn: isServicesMenu ? undefined : `1 / span ${tertiaryColumnCount}`,
            }}
          >
            {renderQuickLinks(quickLinks, quickLinksHeading)}
          </div>
        )}
      </div>
    </ul>
  );
};

const renderSecondaryNavDetails = (
  secondaryNavDetails: any[],
  parentNavTitle?: string,
  openTertiaryOnLeft?: boolean,
  matchedHeight?: number | null,
  onSecondaryHoverChange?: (isHovered: boolean) => void,
) =>
  secondaryNavDetails.map((detail: any, secIdx: number) => {
    const tertiaryNav =
      detail?.xfContent?.children?.[0]?.children?.find(
        (child: any) => child.nodeName === "tertiaryLinks",
      )?.children || [];

    const quickLinks =
      detail?.xfContent?.children?.[0]?.children?.find(
        (child: any) => child.nodeName === "quickLinks",
      )?.children || [];

    const quickLinksHeading =
      detail?.xfContent?.children?.[0]?.quickLinksHeading;

    return (
      <li
        className="group/submenu hover:bg-blue-900"
        key={`${detail.id || detail.secondaryNavLabel || "secondary-nav-item"}-${secIdx}`}
        onMouseEnter={() => onSecondaryHoverChange?.(true)}
        onMouseLeave={() => onSecondaryHoverChange?.(false)}
      >
        <BFLLink
          href={detail?.ctaLink}
          className="block cursor-pointer whitespace-nowrap px-4 py-[10px]"
        >
          {detail.secondaryNavLabel}
        </BFLLink>

        <Conditional
          if={!!(detail?.primaryCtaText || detail?.secondaryCtaText)}
        >
          <span className="hidden px-4 py-[10px] group-hover/submenu:block">
            {detail?.primaryCtaText && (
              <BFLLink
                className="from-link-primary mr-2 cursor-pointer rounded-[25px] bg-gradient-to-r to-[#ff8900] px-3 py-1 text-xs"
                href={detail?.primaryCtaLink}
              >
                {detail.primaryCtaText}
              </BFLLink>
            )}
            {detail?.secondaryCtaText && (
              <BFLLink
                className="text-link-primary cursor-pointer text-sm font-[500]"
                href={detail?.secondaryCtaLink}
              >
                {detail.secondaryCtaText}
              </BFLLink>
            )}
          </span>
        </Conditional>

        {(tertiaryNav.length > 0 || quickLinks.length > 0) &&
          renderTertiaryNavWithQuickLinks(
            tertiaryNav,
            quickLinks,
            quickLinksHeading,
            parentNavTitle,
            openTertiaryOnLeft,
            undefined,
            matchedHeight,
          )}
      </li>
    );
  });

interface ScrollbarState {
  visible: boolean;
  thumbHeight: number;
  thumbTop: number;
  trackHeight: number;
}

const SCROLLBAR_MIN_THUMB_HEIGHT = 40;

const SubmenuScrollList: FC<{ children: ReactNode; height?: number | null }> = ({
  children,
  height,
}) => {
  const listRef = useRef<HTMLUListElement>(null);
  const [scrollbar, setScrollbar] = useState<ScrollbarState>({
    visible: false,
    thumbHeight: 0,
    thumbTop: 0,
    trackHeight: 0,
  });

  const updateScrollbar = useCallback(() => {
    const listEl = listRef.current;
    if (!listEl) return;

    const { scrollHeight, clientHeight, scrollTop } = listEl;
    const hasOverflow = scrollHeight > clientHeight + 1;

    if (!hasOverflow || clientHeight === 0) {
      setScrollbar({
        visible: false,
        thumbHeight: 0,
        thumbTop: 0,
        trackHeight: 0,
      });
      return;
    }

    const thumbHeight = Math.max(
      (clientHeight / scrollHeight) * clientHeight,
      SCROLLBAR_MIN_THUMB_HEIGHT,
    );
    const maxThumbTop = clientHeight - thumbHeight;
    const scrollRatio = scrollTop / (scrollHeight - clientHeight);
    const thumbTop = scrollRatio * maxThumbTop;

    setScrollbar({
      visible: true,
      thumbHeight,
      thumbTop,
      trackHeight: clientHeight,
    });
  }, []);

  useEffect(() => {
    const listEl = listRef.current;
    if (!listEl) return;

    updateScrollbar();

    let resizeObserver: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updateScrollbar);
      resizeObserver.observe(listEl);

      const parentEl = listEl.parentElement?.parentElement;
      if (parentEl) {
        resizeObserver.observe(parentEl);
      }
    }

    const mutationObserver = new MutationObserver(updateScrollbar);
    mutationObserver.observe(listEl, { childList: true, subtree: true });

    const initialCheck = window.setTimeout(updateScrollbar, 0);
    const fallbackCheck = window.setTimeout(updateScrollbar, 150);

    return () => {
      resizeObserver?.disconnect();
      mutationObserver.disconnect();
      window.clearTimeout(initialCheck);
      window.clearTimeout(fallbackCheck);
    };
  }, [children, updateScrollbar]);

  return (
    <div
      className="relative"
      style={height ? { height: `${height}px` } : undefined}
    >
      <ul
        ref={listRef}
        onScroll={updateScrollbar}
        className="header-submenu-scroll-content m-0 list-none p-0"
        style={height ? { height: "100%" } : undefined}
      >
        {children}
      </ul>
      {scrollbar.visible && (
        <div
          aria-hidden
          className="pointer-events-none absolute right-0 top-0 w-1.5"
          style={{ height: scrollbar.trackHeight }}
        >
          <div
            className="header-submenu-scrollbar-thumb absolute right-0"
            style={{ height: scrollbar.thumbHeight, top: scrollbar.thumbTop }}
          />
        </div>
      )}
    </div>
  );
};

const LeftMenu: FC<LeftMenuProps> = ({
  navItems = [],
  bottomnavigationmobile,
}) => {
  const showSecondaryNav = true;
  const [submenuHovered, setSubmenuHovered] = useState(false);
  const [secondaryItemHovered, setSecondaryItemHovered] = useState(false);
  const [hoveredItemIndex, setHoveredItemIndex] = useState<number | null>(null);
  const [secondaryDropdownHeight, setSecondaryDropdownHeight] = useState<
    number | null
  >(null);
  const [openTertiaryOnLeft, setOpenTertiaryOnLeft] = useState(false);
  const secondaryDropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!secondaryDropdownRef.current) return;
    if (!submenuHovered || hoveredItemIndex === null) return;

    let animationFrameId = 0;
    let resizeObserver: ResizeObserver | undefined;

    const measureDropdown = () => {
      const node = secondaryDropdownRef.current;
      if (!node) return;

      const rect = node.getBoundingClientRect();
      const nextWidth = rect.width || node.offsetWidth || node.clientWidth;
      const tertiaryEstimatedWidth = Math.max(nextWidth, 360);
      const availableRightSpace = window.innerWidth - rect.right;
      const secondaryListNode = node.querySelector<HTMLElement>(
        ".header-submenu-scroll-content",
      );
      const tertiaryNode = node.querySelector<HTMLElement>(".tertiary-menu");
      const secondaryHeight = secondaryListNode
        ? secondaryListNode.scrollHeight ||
          secondaryListNode.getBoundingClientRect().height ||
          secondaryListNode.offsetHeight ||
          secondaryListNode.clientHeight
        : 0;
      const tertiaryHeight = tertiaryNode
        ? tertiaryNode.getBoundingClientRect().height ||
          tertiaryNode.offsetHeight ||
          tertiaryNode.clientHeight
        : 0;
      const nextHeight = Math.max(secondaryHeight, tertiaryHeight);

      if (nextHeight > 0) {
        setSecondaryDropdownHeight(nextHeight);
      }

      setOpenTertiaryOnLeft(availableRightSpace < tertiaryEstimatedWidth);
    };

    animationFrameId = window.requestAnimationFrame(() => {
      measureDropdown();
      window.requestAnimationFrame(measureDropdown);
    });

    if (typeof ResizeObserver !== "undefined" && secondaryDropdownRef.current) {
      resizeObserver = new ResizeObserver(measureDropdown);
      resizeObserver.observe(secondaryDropdownRef.current);
      const tertiaryNode = secondaryDropdownRef.current.querySelector<HTMLElement>(
        ".tertiary-menu",
      );
      if (tertiaryNode) {
        resizeObserver.observe(tertiaryNode);
      }
    }

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      resizeObserver?.disconnect();
    };
  }, [submenuHovered, hoveredItemIndex]);

  useEffect(() => {
    document.body.classList.toggle(
      "desktop-submenu-active",
      secondaryItemHovered,
    );

    return () => {
      document.body.classList.remove("desktop-submenu-active");
    };
  }, [secondaryItemHovered]);

  return (
    <div className="md:max-w-265.25 relative z-20 flex">
      <MenuToggle bottomnavigationmobile={bottomnavigationmobile} />
      <ul className="flex" data-testid="main-nav-list">
          {navItems.length > 0 ? (
            navItems.map((item: any, itemIdx: number) => {
              const secondaryNavDetails =
                item?.xfContent?.children?.[0]?.children?.find(
                  (child: any) => child.nodeName === "secondaryNavDetails",
                )?.children || [];

              return (
                <li
                  className="group relative flex items-center gap-1 py-2.5 leading-5 hover:bg-blue-900 md:px-px md:text-[14px] lg:px-1.5 lg:text-sm font-[Rubik,Mukta,BalooThambi2,sans-serif] font-medium"
                  key={`${item.id || item.nodeName || item.topNavTitle || "top-nav-item"}-${itemIdx}`}
                  onMouseEnter={() => {
                    setHoveredItemIndex(itemIdx);
                    setSubmenuHovered(true);
                  }}
                  onMouseLeave={() => {
                    setSubmenuHovered(false);
                    setSecondaryItemHovered(false);
                    setHoveredItemIndex(null);
                    setSecondaryDropdownHeight(null);
                    setOpenTertiaryOnLeft(false);
                  }}
                >
                  {item.topNavTitle || item.nodeName}
                  <i className="icomoon text-sm transition-transform duration-200 group-hover:rotate-180 before:content-['\e909']"></i>
                  {showSecondaryNav &&
                    submenuHovered &&
                    hoveredItemIndex === itemIdx &&
                    secondaryNavDetails.length > 0 && (
                      <div
                        ref={secondaryDropdownRef}
                        role="presentation"
                        className="absolute top-full z-20 hidden bg-blue-500 min-w-70 left-0 group-hover:block"
                        onMouseEnter={() => {
                          setSecondaryItemHovered(true);
                        }}
                        onMouseLeave={() => {
                          setSecondaryItemHovered(false);
                        }}
                        style={
                          secondaryDropdownHeight
                            ? { height: `${secondaryDropdownHeight}px` }
                            : undefined
                        }
                      >
                        <SubmenuScrollList height={secondaryDropdownHeight}>
                          {renderSecondaryNavDetails(
                            secondaryNavDetails,
                            item.topNavTitle,
                            openTertiaryOnLeft,
                            secondaryDropdownHeight,
                            setSecondaryItemHovered,
                          )}
                        </SubmenuScrollList>
                      </div>
                    )}
                </li>
              );
            })
          ) : (
            <li className="px-3 py-2.5 text-xs lg:text-sm">
              No navigation items available
            </li>
          )}
      </ul>
    </div>
  );
};

export default LeftMenu;
